import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { INDUSTRIES } from '../../data/industries.data';
import { QuotationFormData } from '../../interfaces/quotation.interface';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, HttpHeaders} from '@angular/common/http';
import { SnackService } from 'src/app/shared/components/snack/snack.service';
@Component({
  selector: 'app-quotation-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    HttpClientModule
  ],
  template: `
    <!-- Root container with dynamic text direction -->
    <div class="quotation-form-container" [dir]="direction">
      <h2 mat-dialog-title>
        <mat-icon>request_quote</mat-icon>
        {{ dialogTitle }}
      </h2>

      <mat-dialog-content>
        <form #quotationForm="ngForm">
          <div class="form-section">
            <h3>{{ companyInfoHeader }}</h3>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelCompanyName }}</mat-label>
              <input matInput [(ngModel)]="formData.companyName" name="companyName" required>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelContactPerson }}</mat-label>
              <input matInput [(ngModel)]="formData.contactPerson" name="contactPerson" required>
            </mat-form-field>
          </div>

          <div class="form-section">
            <h3>{{ contactDetailsHeader }}</h3>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelEmail }}</mat-label>
              <input matInput type="email" [(ngModel)]="formData.email" name="email" required>
              <mat-icon matSuffix>email</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelPhone }}</mat-label>
              <input matInput [(ngModel)]="formData.phoneNumber" name="phone" required>
              <mat-icon matSuffix>phone</mat-icon>
            </mat-form-field>
          </div>

          <div class="form-section">
            <h3>{{ businessDetailsHeader }}</h3>
            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>{{ labelBranches }}</mat-label>
                <input matInput type="number" [(ngModel)]="formData.numberOfBranches" name="branches" required min="1">
                <mat-icon matSuffix>store</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ labelUsers }}</mat-label>
                <input matInput type="number" [(ngModel)]="formData.numberOfUsers" name="users" required min="1">
                <mat-icon matSuffix>group</mat-icon>
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelIndustry }}</mat-label>
              <mat-select [(ngModel)]="formData.industry" name="industry" required>
                <mat-option *ngFor="let industry of industries" [value]="industry.value">
                  {{ industry.label | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field *ngIf="formData.industry === 'other'" appearance="outline" class="full-width">
              <mat-label>{{ labelSpecifyIndustry }}</mat-label>
              <input matInput [(ngModel)]="formData.otherIndustry" name="otherIndustry" required>
            </mat-form-field>
          </div>

          <div class="form-section">
            <h3>{{ additionalInfoHeader }}</h3>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ labelRequirements }}</mat-label>
              <textarea matInput [(ngModel)]="formData.requirements" name="requirements" rows="4"
                placeholder="{{ placeholderRequirements }}"></textarea>
            </mat-form-field>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">{{ cancelButtonText }}</button>
        <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="!quotationForm.form.valid">
          <mat-icon>send</mat-icon>
          {{ submitButtonText }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .quotation-form-container {
      padding: 1rem;
    }

    /* Ensure text aligns correctly in both LTR and RTL */
    .quotation-form-container[dir="ltr"] {
      text-align: left;
    }
    .quotation-form-container[dir="rtl"] {
      text-align: right;
    }

    mat-dialog-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #6a1b9a;
    }

    .form-section {
      margin-bottom: 2rem;
    }

    .form-section h3 {
      color: #6a1b9a;
      margin-bottom: 1rem;
      font-size: 1.1rem;
      font-weight: 500;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    .full-width {
      width: 100%;
    }

    mat-form-field {
      margin-bottom: 1rem;
    }

    textarea {
      resize: vertical;
      min-height: 100px;
    }

    mat-dialog-actions {
      padding: 1rem 0;
    }

    button mat-icon {
      margin-right: 0.5rem;
    }

    @media (max-width: 600px) {
      .form-row {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class QuotationFormComponent implements OnInit {
  industries = INDUSTRIES;

  formData: QuotationFormData = {
    companyName: '',
    contactPerson: '',
    email: '',
    phoneNumber: '',
    numberOfBranches: 1,
    numberOfUsers: 1,
    industry: '',
    otherIndustry:'',
    requirements: ''
  };

  // Localized text properties
  dialogTitle: string = '';
  companyInfoHeader: string = '';
  contactDetailsHeader: string = '';
  businessDetailsHeader: string = '';
  additionalInfoHeader: string = '';
  labelCompanyName: string = '';
  labelContactPerson: string = '';
  labelEmail: string = '';
  labelPhone: string = '';
  labelBranches: string = '';
  labelUsers: string = '';
  labelIndustry: string = '';
  labelSpecifyIndustry: string = '';
  labelRequirements: string = '';
  placeholderRequirements: string = '';
  cancelButtonText: string = '';
  submitButtonText: string = '';

  // Property to set text direction; default is LTR.
  direction: string = 'ltr';

  constructor(
    private dialogRef: MatDialogRef<QuotationFormComponent>,
    private _translate: TranslateService,
    private http: HttpClient,
    private _snack: SnackService
  ) {}

  ngOnInit(): void {
    // Load localized texts
    this.dialogTitle = this._translate.instant('QUOTATION_FORM.DIALOG_TITLE');
    this.companyInfoHeader = this._translate.instant('QUOTATION_FORM.COMPANY_INFORMATION');
    this.contactDetailsHeader = this._translate.instant('QUOTATION_FORM.CONTACT_DETAILS');
    this.businessDetailsHeader = this._translate.instant('QUOTATION_FORM.BUSINESS_DETAILS');
    this.additionalInfoHeader = this._translate.instant('QUOTATION_FORM.ADDITIONAL_INFORMATION');
    this.labelCompanyName = this._translate.instant('QUOTATION_FORM.LABEL_COMPANY_NAME');
    this.labelContactPerson = this._translate.instant('QUOTATION_FORM.LABEL_CONTACT_PERSON');
    this.labelEmail = this._translate.instant('QUOTATION_FORM.LABEL_EMAIL');
    this.labelPhone = this._translate.instant('QUOTATION_FORM.LABEL_PHONE');
    this.labelBranches = this._translate.instant('QUOTATION_FORM.LABEL_BRANCHES');
    this.labelUsers = this._translate.instant('QUOTATION_FORM.LABEL_USERS');
    this.labelIndustry = this._translate.instant('QUOTATION_FORM.LABEL_INDUSTRY');
    this.labelSpecifyIndustry = this._translate.instant('QUOTATION_FORM.LABEL_SPECIFY_INDUSTRY');
    this.labelRequirements = this._translate.instant('QUOTATION_FORM.LABEL_REQUIREMENTS');
    this.placeholderRequirements = this._translate.instant('QUOTATION_FORM.PLACEHOLDER_REQUIREMENTS');
    this.cancelButtonText = this._translate.instant('QUOTATION_FORM.CANCEL_BUTTON');
    this.submitButtonText = this._translate.instant('QUOTATION_FORM.SUBMIT_BUTTON');

    // Set text direction based on current language (assumes 'ar' for Arabic)
    this.direction = this._translate.currentLang === 'ar' ? 'rtl' : 'ltr';
  }

  onSubmit() {
    const submitData = {
      ...this.formData,
      industry: this.formData.industry === 'other' ? this.formData.otherIndustry : this.formData.industry
    };
    // this.dialogRef.close(submitData);
    this.http.post('api/v1/email/request-quotation', submitData).subscribe(response => {
      console.log('Quotation request sent successfully', response);
      if(response){
        this._snack.showSnack('quotation sent successfully', 'success');
        // this.contactForm.reset();
      }
      this.dialogRef.close(submitData);
    }, error => {
      console.error('Error sending quotation request', error);
    });
  }

  onCancel() {
    this.dialogRef.close();
  }
}
